import { useEffect, useState } from "react";
import shortid from "shortid";
import {
  Grid,
  TextField,
  Button,
  FormControl,
  Autocomplete,
  CurrencyInput,
  DataGrid,
  Typography,
} from "../../../../../components";
import { value } from "../../../../../helpers";
import {
  formatDate,
  unformatPrice,
  formatPrice,
  formatPrecisaoFloat,
} from "../../../../../utils";
import { notification } from "../../../../../services";
import { parcelaData } from "../../add-nf-saida";
import { NotasCreditoDialog } from "..";
import { useImportContext } from "../../../../../contexts/import.context";

const NfSaidaPagamentos = ({
  nfSaidaId,
  nfSaida,
  setNfSaida,
  nfSaidaEntity,
  nfSaidaPagamentos,
  setNfSaidaPagamentos,
  meioPagamentoList,
  meioPagamentoSelecionado,
  setMeioPagamentoSelecionado,
  condicaoSelecionada,
  setCondicaoSelecionada,
  meiosPagamentoCliente,
  gerarPagamentos,
  setGerarPagamentos,
  condicaoPagParcelaList,
  setCondicaoPagParcelaList,
  listaNotasCredito,
  setListaNotasCredito,
  openNotasCreditoDialog,
  setOpenNotasCreditoDialog,
  notasCredito,
  setNotasCredito,
  meioPagamentoCredito,
  buscarNotasCredito,
  setAbaValue,
  obrigatoriedadeMeioPagamento,
  nfSaidaProdutos,
  setNfSaidaProdutos,
  setAcrescimoPagamento,
  item,
  setItem,
  produto,
  clienteSelecionado,
}) => {
  const { nfSaidaDto } = useImportContext();
  const [inputError, setInputError] = useState(null);
  const [saldo, setSaldo] = useState(0);
  const colunas = [
    {
      field: "meioPagamentoId",
      headerName: "Meio de Pagamento",
      flex: 700,
      sortable: false,
      valueGetter: (params) =>
        value.autoComplete(meioPagamentoList, params.value).descricao,
    },
    {
      field: "valorTitulo",
      headerName: "Valor do Título",
      flex: 700,
      sortable: false,
      valueGetter: (params) =>
        params.value > 0 ? formatPrice(params.value) : "R$ 0,00",
    },
    {
      field: "dataVencimento",
      headerName: "Vencimento",
      flex: 700,
      type: "date",
      editable: !nfSaida.id ? true : false,
      sortable: false,
      valueGetter: (params) => {
        return new Date(formatDate.format(params.value));
      },
    },
    {
      field: "numeroAutorizacao",
      headerName: "Nº Autorização",
      flex: 500,
      editable: !nfSaida.id ? true : false,
      sortable: false,
      valueGetter: (params) => params.value ?? "",
    },
  ];

  useEffect(() => {
    const valorTitulo =
      parseFloat(nfSaida?.totalVenda ?? 0) -
      parseFloat(nfSaida?.totalPagamentos ?? 0);
    setSaldo(valorTitulo);
    if (valorTitulo > 0) {
      setGerarPagamentos((prevState) => ({
        ...prevState,
        valorTitulo,
      }));
    }
  }, [nfSaida.totalVenda, nfSaida.totalPagamentos]);

  const onChangeMeioPagamento = (id, value) => {
    const condicoesPagamento = value?.condicoesPagamento ?? [];
    setMeioPagamentoSelecionado(value);
    let acrescimo = null;
    let gerarPagamentosTemp = {
      [id]: value?.id ?? null,
      condicaoPagamentoId: null,
      quantidadeParcelas: null,
    };
    const primeiraCondicao = condicoesPagamento[0];
    const condicaoPagamentoUnica = condicoesPagamento.length === 1;
    if (condicaoPagamentoUnica) {
      acrescimo = primeiraCondicao?.pagamentoInfo?.acrescimo ?? null;
      gerarPagamentosTemp = {
        [id]: value?.id ?? null,
        condicaoPagamentoId: primeiraCondicao?.id ?? null,
        quantidadeParcelas: primeiraCondicao?.parcelas ?? null,
      };
    }
    setGerarPagamentos((prev) => ({
      ...prev,
      ...gerarPagamentosTemp,
    }));
    setCondicaoSelecionada(condicaoPagamentoUnica ? primeiraCondicao : null);
    setAcrescimoPagamento(acrescimo);
    recalcularProdutosAcrescimoPagamento(acrescimo);
    setCondicaoPagParcelaList(condicoesPagamento);
    if (!nfSaidaPagamentos.length) {
      setAbaValue(
        condicaoPagamentoUnica && obrigatoriedadeMeioPagamento ? 0 : 1,
      );
    }
  };

  function recalcularProdutosAcrescimoPagamento(acrescimo) {
    const calculoValorUnitario = (acrescimo, produto) =>
      acrescimo > 0
        ? formatPrecisaoFloat(
            produto?.valor + produto?.valor * (acrescimo / 100),
          )
        : produto?.valor;
    const calculoDesconto = (valor, produto) =>
      produto?.descontoPorcentagem
        ? (valor *
            produto?.quantidade *
            parseFloat(produto?.descontoPorcentagem)) /
          100
        : produto?.desconto;
    const valor = calculoValorUnitario(acrescimo, item);
    if (item) {
      setItem((prevState) => ({
        ...(produto || {}),
        ...prevState,
        valor,
        subtotal: valor * prevState?.quantidade,
        desconto: calculoDesconto(valor, prevState),
      }));
    }
    if (nfSaidaProdutos.length) {
      const listaItens = nfSaidaProdutos.map((nfSaidaItem) => {
        const valor = calculoValorUnitario(acrescimo, nfSaidaItem);
        const desconto = calculoDesconto(valor, nfSaidaItem);
        return {
          ...nfSaidaItem,
          valor,
          desconto,
          subtotal: valor * nfSaidaItem?.quantidade,
        };
      });
      setNfSaidaProdutos(listaItens);
      nfSaidaEntity.setChangeItens(nfSaidaId, listaItens);
      setNfSaida({ ...nfSaidaEntity });
    }
  }

  const onChangeAutocomplete = (name, value) => {
    const pedidoPagamentoTemp = {
      ...gerarPagamentos,
      [name]: value ? value.id : null,
    };
    if (name === "condicaoPagamentoId") {
      const acrescimo = value?.pagamentoInfo?.acrescimo;
      setCondicaoSelecionada(value);
      setAcrescimoPagamento(acrescimo);
      recalcularProdutosAcrescimoPagamento(acrescimo);
      pedidoPagamentoTemp.quantidadeParcelas = value?.parcelas;
      setAbaValue(value && obrigatoriedadeMeioPagamento ? 0 : 1);
    }
    setGerarPagamentos(pedidoPagamentoTemp);
  };

  const handleChangeValor = (event) => {
    const pagamentoTemp = {
      ...gerarPagamentos,
      [event.target.name]:
        parseFloat(unformatPrice(event.target.value)) || null,
    };
    setGerarPagamentos(pagamentoTemp);
  };

  const regexNumeroAutorizacao = (numeroAutorizacao) =>
    `${numeroAutorizacao}`
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 25)
      .toUpperCase();

  const handleChangeInput = (event) => {
    let valor = event.target.value;
    if (event.target.name === "numeroAutorizacao") {
      valor = valor ? regexNumeroAutorizacao(valor) : null;
    } else {
      valor = valor ? `${valor}`.toUpperCase() : null;
    }
    setGerarPagamentos({ ...gerarPagamentos, [event.target.name]: valor });
  };

  function validarParcela() {
    if (!gerarPagamentos?.meioPagamentoId) {
      setInputError("meioPagamentoId");
      notification.erroValidacao("Meio de pagamento");
      return true;
    }
    if (!gerarPagamentos?.condicaoPagamentoId) {
      setInputError("condicaoPagamentoId");
      notification.erroValidacao("Condição de pagamento");
      return true;
    }
    if (!gerarPagamentos?.quantidadeParcelas) {
      setInputError("quantidadeParcelas");
      notification.erroValidacao("Quantidade de Parcelas");
      return true;
    }
    if (!gerarPagamentos?.valorTitulo) {
      setInputError("valorTitulo");
      notification.erroValidacao("Valor do título");
      return true;
    }
    if (
      meioPagamentoSelecionado?.cartaoCreditoDebito &&
      !gerarPagamentos?.numeroAutorizacao
    ) {
      setInputError("numeroAutorizacao");
      notification.erroValidacao("Número de Autorização");
      return true;
    }
    if (
      parseFloat(nfSaida?.totalPagamentos || 0) +
        parseFloat(gerarPagamentos.valorTitulo || 0) >
      (nfSaida?.totalVenda || 0)
    ) {
      if (meioPagamentoSelecionado?.nfePagamento === "fpDinheiro") return;
      notification.alertaGenericos(
        "Valor inserido ultrapassa total da Nota fiscal",
      );
      return true;
    }
    return false;
  }

  const handleGerarParcelas = () => {
    const parcelaIsValid = validarParcela();
    if (parcelaIsValid) return;
    if (
      nfSaidaDto.documentoDevolucaoCliente ||
      nfSaidaDto.documentoDevolucaoFornecedor
    ) {
      gerarPagamentos.quantidadeParcelas = 1;
    }
    const valorParcela = formatPrecisaoFloat(
      gerarPagamentos.valorTitulo / gerarPagamentos.quantidadeParcelas,
    );
    const valorFormatado = formatPrecisaoFloat((valorParcela * 100) / 100);
    const restoDivisao = formatPrecisaoFloat(
      gerarPagamentos.valorTitulo -
        valorFormatado * gerarPagamentos.quantidadeParcelas,
    );
    const parcelasGeradas = [];
    for (
      let contador = 1;
      contador <= gerarPagamentos?.quantidadeParcelas;
      contador++
    ) {
      const valorTitulo =
        contador === gerarPagamentos.quantidadeParcelas
          ? formatPrecisaoFloat(valorFormatado + parseFloat(restoDivisao))
          : valorFormatado;
      parcelasGeradas.push({
        id: shortid.generate(),
        numeroTitulo: nfSaidaPagamentos.length
          ? nfSaidaPagamentos.length + contador
          : contador,
        condicaoPagamentoId: gerarPagamentos?.condicaoPagamentoId,
        numeroAutorizacao: gerarPagamentos?.numeroAutorizacao,
        meioPagamentoId: gerarPagamentos?.meioPagamentoId,
        meioPagamentoSelecionado,
        valorTitulo,
        dataVencimento: parcelaData(
          contador,
          nfSaida?.dataEmissao,
          condicaoSelecionada?.periodo,
        ),
      });
    }
    setNfSaidaPagamentos((prevState) => [...prevState, ...parcelasGeradas]);
    nfSaidaEntity.setChangePagamentos([
      ...nfSaidaPagamentos,
      ...parcelasGeradas,
    ]);
    setNfSaida(nfSaidaEntity);
    const valorRestante =
      nfSaidaEntity?.totalVenda -
      parseFloat(nfSaidaEntity?.totalPagamentos ?? 0);
    setGerarPagamentos({
      valorTitulo: valorRestante >= 0 ? valorRestante : 0,
    });
    notification.sucessoGenericos("Parcelas geradas com sucesso");
  };

  const onSubmitParcelas = (event) => {
    event.preventDefault();
    handleGerarParcelas();
  };

  const onClickDeletePagamentos = async (idParcela) => {
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir essa parcela?`,
    );
    const pagamento = nfSaidaPagamentos.find(({ id }) => id == idParcela);
    if (retornoAlerta.isConfirmed) {
      if (
        pagamento?.meioPagamentoSelecionado?.nfePagamento == "fpCreditoEmLoja"
      ) {
        setNfSaidaPagamentos([]);
        nfSaidaEntity.setChangePagamentos([]);
        setNfSaida(nfSaidaEntity);
        setAbaValue(0);
        buscarNotasCredito(clienteSelecionado.id);
      } else {
        const data = nfSaidaPagamentos.filter((item) => item.id !== idParcela);
        setGerarPagamentos({
          ...gerarPagamentos,
          valorTitulo:
            gerarPagamentos?.valorTitulo +
            pagamento?.valorTitulo.toFixed(2) * 1,
        });
        setNfSaidaPagamentos(data);
        nfSaidaEntity.setChangePagamentos(data);
        setNfSaida(nfSaidaEntity);
      }
    }
  };

  const onClickLimparParcelas = async () => {
    if (nfSaidaPagamentos.length > 0) {
      const retornoAlerta = await notification.confirmacao(
        "Excluir!",
        `Tem certeza que deseja excluir todos os pagamentos efetuados?`,
      );
      if (retornoAlerta.isConfirmed) {
        const notaCredito = nfSaidaPagamentos.filter(
          (item) =>
            item.meioPagamentoSelecionado?.nfePagamento == "fpCreditoEmLoja",
        );
        if (notaCredito.length > 0) {
          setNfSaidaPagamentos([]);
          nfSaidaEntity.setChangePagamentos([]);
          setNfSaida(nfSaidaEntity);
          setAbaValue(0);
          buscarNotasCredito(clienteSelecionado.id);
        } else {
          setGerarPagamentos({
            ...gerarPagamentos,
            valorTitulo: nfSaidaEntity?.totalPagamentos,
          });
          setNfSaidaPagamentos([]);
          nfSaidaEntity.setChangePagamentos(null);
          setNfSaida(nfSaidaEntity);
        }
      }
    }
  };

  const onCellKeyDown = (params, event) => {
    if (event.key !== "Enter" && event.key !== "Tab") return;
    let updatedValue = event.target.value ? event.target.value : null;
    if (params.field === "dataVencimento") {
      updatedValue = formatDate.toSend(formatDate.format(updatedValue));
    }
    const pagamentos = nfSaidaPagamentos.map((pagamento) => {
      const isMatchingPagamento =
        params.row.meioPagamentoId === pagamento.meioPagamentoId;
      if (
        isMatchingPagamento &&
        (params.field === "numeroAutorizacao" || params.row.id === pagamento.id)
      ) {
        let fieldValue =
          params.field === "numeroAutorizacao"
            ? updatedValue
              ? regexNumeroAutorizacao(event.target.value)
              : null
            : updatedValue;
        return { ...pagamento, [params.field]: fieldValue };
      }
      return pagamento;
    });
    setNfSaidaPagamentos(pagamentos);
    nfSaidaEntity.setChangePagamentos(pagamentos);
    setNfSaida(nfSaidaEntity);
  };

  return (
    <>
      <Grid container>
        <Grid container spacing={2} xs={4} className="d-block">
          <Grid item xs={12}>
            <Autocomplete
              id="meioPagamentoId"
              name="meioPagamentoId"
              disabled={nfSaidaId}
              options={
                nfSaida.documentoDevolucaoCliente ||
                nfSaida.documentoDevolucaoFornecedor
                  ? meioPagamentoList
                  : meiosPagamentoCliente ??
                    meioPagamentoList.filter(
                      (meio) => meio.nfePagamento !== "fpCreditoEmLoja",
                    )
              }
              autoHighlight
              getOptionLabel={(option) =>
                option.descricao ? option.descricao : ""
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Meio de Pagamento"
                  variant="outlined"
                  margin="normal"
                  error={inputError == "meioPagamentoId"}
                  required
                />
              )}
              onChange={(e, newValue) => {
                onChangeMeioPagamento("meioPagamentoId", newValue);
              }}
              value={value.autoComplete(
                meioPagamentoList,
                gerarPagamentos?.meioPagamentoId,
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                required
                id="condicaoPagamentoId"
                name="condicaoDePagamento"
                disabled={nfSaidaId}
                options={condicaoPagParcelaList}
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao ? option.descricao : ""
                }
                getOptionSelected={(option, value) =>
                  option?.descricao === value?.descricao
                }
                noOptionsText="Sem opções"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Condição de Pagamento"
                    variant="outlined"
                    error={inputError == "condicaoPagamentoId"}
                    required
                  />
                )}
                onChange={(e, newValue) => {
                  e.persist();
                  onChangeAutocomplete("condicaoPagamentoId", newValue);
                }}
                value={value.autoComplete(
                  condicaoPagParcelaList,
                  gerarPagamentos?.condicaoPagamentoId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <CurrencyInput
              id="valorTitulo"
              name="valorTitulo"
              label="Valor do Título (R$)"
              inputVariant="outlined"
              margin="normal"
              required
              disabled={nfSaidaId}
              error={inputError == "valorTitulo"}
              value={gerarPagamentos?.valorTitulo ?? ""}
              onBlur={(event) => {
                setInputError(null);
                handleChangeValor(event);
              }}
              fullWidth
            />
          </Grid>
          {meioPagamentoSelecionado?.cartaoCreditoDebito && (
            <Grid item xs={12}>
              <TextField
                id="numeroAutorizacao"
                name="numeroAutorizacao"
                label="Número Autorização"
                inputVariant="outlined"
                margin="none"
                required
                disabled={nfSaidaId}
                error={inputError == "numeroAutorizacao"}
                value={gerarPagamentos?.numeroAutorizacao ?? ""}
                inputProps={{ maxLength: 25 }}
                onChange={(event) => {
                  setInputError(null);
                  handleChangeInput(event);
                }}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12} fullWidth>
            <Button
              fullWidth
              className="mt-3 mb-3"
              size="large"
              variant="contained"
              color="primary"
              disabled={nfSaidaId}
              onClick={onSubmitParcelas}
            >
              <i
                className="ph-fill ph-plus-circle"
                style={{ color: "#fff", fontSize: 22, marginRight: 10 }}
              ></i>
              Gerar Parcelas
            </Button>
          </Grid>
        </Grid>
        <Grid
          container
          xs={1}
          fullWidth
          className="d-flex justify-content-center"
        >
          <hr className="vr" />
        </Grid>
        <Grid container xs={7} className="d-block mt-3">
          <Grid
            container
            xs={12}
            fullWidth
            className="d-flex justify-content-between align-items-center"
          >
            <Typography variant="overline">
              <h4 style={{ fontWeight: 800, margin: 0 }}>PARCELAS</h4>
            </Typography>
            {!nfSaidaId && (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={onClickLimparParcelas}
              >
                <i
                  className="ph-fill ph-trash"
                  style={{ color: "#2196F3", fontSize: 18, marginRight: 5 }}
                ></i>
                Limpar Parcelas
              </Button>
            )}
          </Grid>
          <Grid container xs={12} fullWidth className="mt-4">
            <DataGrid
              rows={nfSaidaPagamentos}
              columns={colunas}
              rowCount={nfSaidaPagamentos.length}
              deleteShow={!nfSaidaId}
              sortModel={[{ field: "condicaoPagamentoId", sort: "desc" }]}
              onClickDelete={onClickDeletePagamentos}
              onCellKeyDown={onCellKeyDown}
              onCellEditStop={(params) => {
                if (params.reason === "cellFocusOut") {
                  const newValue = params.value ?? null;
                  if (newValue !== undefined) {
                    setNfSaidaPagamentos((prev) =>
                      prev.map((r) =>
                        r.id === params.id
                          ? { ...r, [params.field]: newValue }
                          : r,
                      ),
                    );
                  }
                  notification.alertaGenericos(
                    "Pressione Enter para salvar as alterações",
                  );
                }
              }}
            />
            <Grid
              container
              xs={12}
              fullWidth
              className="d-flex align-items-center justify-content-end mt-3"
            >
              <Grid
                item
                xs={5}
                className="d-flex align-items-center justify-content-end"
              >
                <span className="me-2">
                  {saldo >= 0 ? "Saldo a pagar:" : "Troco:"}
                </span>
                <span className="badge bg-secondary bg-gradient bg-gradient text-wrap p-2">
                  {formatPrice(saldo >= 0 ? saldo : saldo * -1) || "R$ 0,00"}
                </span>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <NotasCreditoDialog
        open={openNotasCreditoDialog}
        setOpen={setOpenNotasCreditoDialog}
        nfSaida={nfSaida}
        setNfSaida={setNfSaida}
        nfSaidaEntity={nfSaidaEntity}
        listaNotasCredito={listaNotasCredito}
        setListaNotasCredito={setListaNotasCredito}
        totalVenda={
          (nfSaida?.totalVenda || 0) - (nfSaida?.totalPagamentos || 0)
        }
        notasCredito={notasCredito}
        setNotasCredito={setNotasCredito}
        meioPagamentoCredito={meioPagamentoCredito}
        nfSaidaPagamentos={nfSaidaPagamentos}
        setNfSaidaPagamentos={setNfSaidaPagamentos}
        setGerarPagamentos={setGerarPagamentos}
      />
    </>
  );
};

export default NfSaidaPagamentos;
